import api from './api';

export interface DIYMaintenanceStep {
  id: number;
  title: string;
  description: string;
  tools?: string[];
  safety?: string;
  why?: string;
}

export interface DIYMaintenanceGuide {
  taskTitle: string;
  assetName: string;
  duration: string;
  difficulty: "Easy" | "Medium" | "Hard";
  riskLevel: number;
  steps: DIYMaintenanceStep[];
}

/**
 * Generate DIY maintenance guide using OpenAI API
 */
export async function generateMaintenanceGuide(params: {
  maintenanceTitle: string;
  assetName: string;
  assetBrand?: string;
  assetModel?: string;
  taskId?: string;
}): Promise<DIYMaintenanceGuide> {
  try {
    const response = await api.post<DIYMaintenanceGuide>(
      '/v1/ai/generate-maintenance-guide',
      params
    );
    return response;
  } catch (error) {
    console.error('Failed to generate maintenance guide:', error);
    // Return fallback guide
    return generateFallbackGuide(params.maintenanceTitle, params.assetName);
  }
}

/**
 * Fallback guide in case API fails
 */
function generateFallbackGuide(taskTitle: string, assetName: string): DIYMaintenanceGuide {
  return {
    taskTitle,
    assetName,
    duration: "15-20 minutes",
    difficulty: "Easy",
    riskLevel: 2,
    steps: [
      {
        id: 1,
        title: "Prepare the Area",
        description: "Turn off power to the appliance and prepare your workspace. Gather necessary tools and safety equipment.",
        tools: ["Screwdriver", "Cleaning cloth"],
        safety: "Ensure the appliance is unplugged or power is turned off at the circuit breaker.",
      },
      {
        id: 2,
        title: "Perform Maintenance",
        description: `Follow the manufacturer's instructions for ${taskTitle}. Clean or replace parts as needed.`,
        tools: ["Replacement parts (if needed)", "Cleaning solution"],
        why: "Regular maintenance extends appliance life and maintains efficiency.",
      },
      {
        id: 3,
        title: "Test & Verify",
        description: "Restore power and test the appliance to ensure it's working properly. Monitor for any unusual sounds or behavior.",
        safety: "Stand clear when first powering on. Stop immediately if you notice any issues.",
      },
    ],
  };
}
